import React, { useState, useEffect } from 'react';
import apiFetch from '../utils/apiFetch';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns'; // For date formatting
import { startOfDay, endOfDay } from 'date-fns'; // Import functions for setting the date time to midnight
import 'react-datepicker/dist/react-datepicker.css'; // Styles for react-datepicker
import '../../src/styles/style.css'; 

const Dashboard = () => {
  const [assessmentData, setAssessmentData] = useState([]); // All data
  const [filteredData, setFilteredData] = useState([]);     // Filtered data
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isFiltered, setIsFiltered] = useState(false); // Track if the filter has been applied



  // Fetch data from the server on initial load — default to last 30 days
  useEffect(() => {
    const fetchData = async () => {
      try {
        const defaultEnd = new Date();
        const defaultStart = new Date();
        defaultStart.setDate(defaultStart.getDate() - 30);

        const formattedStartDate = defaultStart.toISOString();
        const formattedEndDate = defaultEnd.toISOString();

        const response = await apiFetch(`/api/assessmentgraph?startDate=${formattedStartDate}&endDate=${formattedEndDate}`);
        const data = await response.json();

        const formattedData = data.map(item => ({
          shortlisting_probability: parseInt(item.shortlisting_probability),
          interview_effectiveness: parseInt(item.interview_effectiveness),
          create_date: new Date(item.create_date),
        }));

        setAssessmentData(formattedData);
        setFilteredData(formattedData);
        // Pre-populate date pickers with the default range
        setStartDate(defaultStart);
        setEndDate(defaultEnd);
        if (formattedData.length > 0) setIsFiltered(true);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Handle date change and filter data based on selected date range
  const handleDateChange = async () => {
    if (startDate && endDate) {
      // Ensure startDate and endDate are valid Date objects
      if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
        console.error('Invalid date format');
        return;
      }
  
      try {
        // Adjust the start and end dates to avoid timezone issues
        const adjustedStartDate = startOfDay(startDate); // Set time to 00:00:00 (start of the day)
        const adjustedEndDate = endOfDay(endDate); // Set time to 23:59:59 (end of the day)
  
        // Add 1 day to the start date
        adjustedStartDate.setDate(adjustedStartDate.getDate() + 1); // Add 1 day to the start date
  
        // Convert the adjusted start date and end date to ISO format (UTC)
        const formattedStartDate = adjustedStartDate.toISOString();
        const formattedEndDate = adjustedEndDate.toISOString();
  
        console.log("Sending Request with dates: ", formattedStartDate, formattedEndDate); // Debugging line to check
  
        // Send the request to the API
        
        const response = await apiFetch(`/api/assessmentgraph?startDate=${formattedStartDate}&endDate=${formattedEndDate}`);
       

        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
  
        const data = await response.json();
  
        // Process and format the fetched data
        const formattedData = data.map(item => ({
          shortlisting_probability: parseInt(item.shortlisting_probability),
          interview_effectiveness: parseInt(item.interview_effectiveness),
          create_date: new Date(item.create_date), // Convert to Date object for easier comparison
        }));
  
        setFilteredData(formattedData);
        setIsFiltered(true); // Mark the filter as applied
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    } else {
      console.error('Start and End dates are required');
    }
  };
  

  // Custom Tooltip for the chart
  const CustomTooltip = ({ payload }) => {
    if (!payload || payload.length === 0) return null;

    return (
      <div className="custom-tooltip">
        {payload.map((entry, index) => (
          <p key={`item-${index}`} style={{ color: entry.stroke }}>
            <strong>{entry.name}: {entry.value}%</strong>
          </p>
        ))}
      </div>
    );
  };

  // Render the trend graph
  return (
    <div className="dashboard-container">
      {/* Filters: Start Date and End Date */}
      <div className="filters" style={{ margin: '20px 0' }}>
        <div className="filter-item" style={{ marginLeft: '10px' }}>
          <label htmlFor="start-date" className="filter-label">Step 1: Select Start Date</label>
          <DatePicker
            id="start-date"
            selected={startDate}
            onChange={date => setStartDate(date)}
            dateFormat="MMM-dd-yyyy" // Short date format
            placeholderText="Select Start Date"
            className="custom-datepicker"
          />
        </div>

        <div className="filter-item" style={{ marginLeft: '10px' }}>
          <label htmlFor="end-date" className="filter-label">Step 2: Select End Date</label>
          <DatePicker
            id="end-date"
            selected={endDate}
            onChange={date => setEndDate(date)}
            dateFormat="MMM-dd-yyyy" // Short date format
            placeholderText="Select End Date"
            className="custom-datepicker"
          />
        </div>

        <button onClick={handleDateChange} className="apply-date-filter-button">
          Apply Date Filter
        </button>
      </div>

      {/* Conditionally render graph and content based on the applied filter */}
      {isFiltered && filteredData.length > 0 && (
        <div className="row" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
           {/* Display the count of records at the top-right corner */}
           <div className="record-count" >
            # of Records: {filteredData.length}
          </div>
          {/* Shortlisting Probability and Interview Effectiveness Trend Graph */}
          <div className="column" style={{ border: '1px solid #ccc', padding: '10px', borderRadius: '5px', margin: '10px', flex: '1 1 100%' }}>
            <h2>Trend Graph: Shortlisting Probability and Interview Effectiveness</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={filteredData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="create_date"
                  tickFormatter={(date) => format(new Date(date), 'dd/MMM')} // Format the date for the X-Axis
                />
                <YAxis />
                {/* Custom Tooltip */}
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="shortlisting_probability"
                  stroke="blue"
                  strokeWidth={3}
                  activeDot={{ r: 8 }}
                  name="Shortlisting Probability"
                />
                <Line
                  type="monotone"
                  dataKey="interview_effectiveness"
                  stroke="orange"
                  strokeWidth={3}
                  activeDot={{ r: 8 }}
                  name="Interview Effectiveness"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* If no data matches the filter */}
      {isFiltered && filteredData.length === 0 && (
        <div className="no-data-message">
          <p>No data available for the selected date range. Please adjust the filter.</p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
